import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../services/supabaseClient.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { YoolTable, StatusPill, MonoText } from '../../../components/ui/YoolTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { RefreshCw, Eye, Activity, CheckCircle2, XCircle, Timer } from 'lucide-react';
import formatters from '../../../styles/tokens/formatters';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import { KpiGrid } from '../../../components/ui/KpiCard';
import ErrorMessage from '../../../components/ErrorMessage';

const STATUS_OPTIONS = ['pending', 'running', 'success', 'error', 'partial', 'skipped'];

const CronRunLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('cron_run_logs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(200);
      if (jobFilter) {
        query = query.ilike('job_name', `%${jobFilter}%`);
      }
      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      if (dateFrom) {
        query = query.gte('started_at', `${dateFrom}T00:00:00Z`);
      }
      if (dateTo) {
        query = query.lte('started_at', `${dateTo}T23:59:59Z`);
      }
      const { data, error: err } = await query;
      if (err) {
        setError(err.message || 'Error al cargar cron_run_logs');
        setRows([]);
      } else {
        setRows(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setError(e.message || 'Error inesperado');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const formatDateTime = (value) => {
    if (!value) return '-';
    return formatters.formatDateTime(value);
  };

  const durationMs = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (Number.isNaN(s) || Number.isNaN(e) || e < s) return null;
    return e - s;
  };

  const formatDuration = (ms) => {
    if (ms == null) return '-';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    if (m < 60) return `${m}m ${s}s`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}m ${s}s`;
  };

  const STATUS_ALIAS = { success: 'completado', error: 'error', running: 'procesando', pending: 'pendiente', partial: 'pendiente', skipped: 'inactivo' };

  const filtered = useMemo(() => {
    let out = [...rows];
    if (jobFilter) {
      const t = jobFilter.toLowerCase();
      out = out.filter((r) => String(r.job_name || '').toLowerCase().includes(t));
    }
    if (statusFilter) {
      out = out.filter((r) => String(r.status || '').toLowerCase() === statusFilter);
    }
    if (dateFrom) {
      const t = new Date(`${dateFrom}T00:00:00Z`).getTime();
      out = out.filter((r) => new Date(r.started_at).getTime() >= t);
    }
    if (dateTo) {
      const t = new Date(`${dateTo}T23:59:59Z`).getTime();
      out = out.filter((r) => new Date(r.started_at).getTime() <= t);
    }
    return out;
  }, [rows, jobFilter, statusFilter, dateFrom, dateTo]);

  const kpis = useMemo(() => {
    const total = filtered.length;
    const success = filtered.filter((r) => String(r.status).toLowerCase() === 'success').length;
    const errorCount = filtered.filter((r) => String(r.status).toLowerCase() === 'error').length;
    const running = filtered.filter((r) => String(r.status).toLowerCase() === 'running').length;
    const durations = filtered
      .map((r) => durationMs(r.started_at, r.finished_at))
      .filter((v) => typeof v === 'number' && v >= 0);
    const avgMs = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
    return { total, success, errorCount, running, avgMs };
  }, [filtered]);

  const kpiItems = useMemo(() => ([
    {
      title: 'Total',
      value: kpis.total,
      icon: Activity,
      accentClass: 'text-slate-700'
    },
    {
      title: 'Éxitos',
      value: kpis.success,
      icon: CheckCircle2,
      accentClass: 'text-[var(--yool-success)]'
    },
    {
      title: 'Errores',
      value: kpis.errorCount,
      icon: XCircle,
      accentClass: 'text-[var(--yool-error)]'
    },
    {
      title: 'Duración prom.',
      value: kpis.avgMs != null ? formatDuration(kpis.avgMs) : '-',
      icon: Timer,
      accentClass: 'text-blue-600'
    }
  ]), [kpis]);

  return (
    <div className="space-y-6">
      <Card className="border-border">
        <CardContent className="pt-6">
          <SectionHeader
            title="Ejecuciones de Crons"
            subtitle="Auditoría y métricas de cron_run_logs"
            action={
              <Button onClick={loadLogs} variant="outline" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            }
          />
          <KpiGrid items={kpiItems} loading={loading} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Input value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} placeholder="Buscar por job_name..." />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todos</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="Desde" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="Hasta" />
            <Button onClick={loadLogs} variant="outline" disabled={loading}>
              Aplicar
            </Button>
          </div>
          {error ? (
            <ErrorMessage
              compact
              title="Error al cargar"
              message={error}
              onRetry={loadLogs}
              className="mt-3"
            />
          ) : null}
        </CardContent>
      </Card>

      <YoolTable
        columns={[
          { key: 'job_name', label: 'Job', sortable: true,
            render: r => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{r.job_name || '-'}</span>
                {r.run_id && <MonoText>{r.run_id}</MonoText>}
              </div>
            )},
          { key: 'status', label: 'Estado', sortable: true,
            render: r => <StatusPill estado={STATUS_ALIAS[String(r.status).toLowerCase()] || r.status}>{r.status}</StatusPill> },
          { key: 'scheduled_at', label: 'Programado', sortable: true, render: r => formatDateTime(r.scheduled_at) },
          { key: 'started_at',   label: 'Inicio',     sortable: true, render: r => formatDateTime(r.started_at) },
          { key: 'finished_at',  label: 'Fin',                        render: r => formatDateTime(r.finished_at) },
          { key: 'duracion', label: 'Duración',
            render: r => formatDuration(durationMs(r.started_at, r.finished_at)) },
          { key: 'rows_read',       label: 'Leídos',          align: 'right', numeric: true, render: r => r.rows_read ?? '-' },
          { key: 'rows_updated',    label: 'Actualizados',    align: 'right', numeric: true, render: r => r.rows_updated ?? '-' },
          { key: 'emails_enqueued', label: 'Emails Encolados',align: 'right', numeric: true, render: r => r.emails_enqueued ?? '-' },
          { key: 'emails_sent',     label: 'Emails Enviados', align: 'right', numeric: true, render: r => r.emails_sent ?? '-' },
          { key: 'detalle', label: '', align: 'right',
            render: r => (
              <Button variant="ghost" size="sm" onClick={() => { setSelected(r); setOpen(true); }}>
                <Eye className="h-4 w-4" />
              </Button>
            )},
        ]}
        rows={filtered}
        rowKey="id"
        initialSort={{ key: 'started_at', dir: 'desc' }}
        empty={{
          icon: 'search',
          title: 'Sin ejecuciones',
          text: jobFilter || statusFilter || dateFrom || dateTo
            ? 'No hay ejecuciones que coincidan con los filtros aplicados.'
            : 'No hay registros de ejecuciones de cron aún.',
        }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Ejecución</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500">Job</div>
                  <div className="font-medium break-words">{selected.job_name || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Estado</div>
                  <div><StatusPill estado={STATUS_ALIAS[String(selected.status).toLowerCase()] || selected.status}>{selected.status}</StatusPill></div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Programado</div>
                  <div>{formatDateTime(selected.scheduled_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Inicio</div>
                  <div>{formatDateTime(selected.started_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Fin</div>
                  <div>{formatDateTime(selected.finished_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Duración</div>
                  <div>{formatDuration(durationMs(selected.started_at, selected.finished_at))}</div>
                </div>
              </div>
              {(() => {
                const parseJson = (val) => {
                  if (!val) return null;
                  if (typeof val === 'object') return val;
                  if (typeof val === 'string') {
                    try {
                      return JSON.parse(val);
                    } catch (_) {
                      return null;
                    }
                  }
                  return null;
                };
                const metricsObj = parseJson(selected.metrics);
                const summary = metricsObj?.summary || null;
                const stats = metricsObj?.stats || null;
                const contextObj = parseJson(selected.context);
                const emailAttempts = Array.isArray(contextObj?.email_attempts) ? contextObj.email_attempts : [];
                return (
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm font-medium mb-2">Resumen</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Confirmaciones</div>
                          <div className="text-lg font-semibold">{summary?.confirmation ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Actualizados</div>
                          <div className="text-lg font-semibold">{summary?.updated ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Errores</div>
                          <div className="text-lg font-semibold text-red-700">{summary?.errors ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Stage 1</div>
                          <div className="text-lg font-semibold">{summary?.stage1 ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Stage 2</div>
                          <div className="text-lg font-semibold">{summary?.stage2 ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Stage 3</div>
                          <div className="text-lg font-semibold">{summary?.stage3 ?? '-'}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium mb-2">Estadísticas</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Emails enviados</div>
                          <div className="text-lg font-semibold">{stats?.resend_sent ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Reintentos hoy</div>
                          <div className="text-lg font-semibold">{stats?.retry_skipped_today ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Reintentos éxitos</div>
                          <div className="text-lg font-semibold">{stats?.retry_success ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Reintentos fallidos</div>
                          <div className="text-lg font-semibold">{stats?.retry_failed ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Processing totales</div>
                          <div className="text-lg font-semibold">{stats?.processing_total ?? '-'}</div>
                        </div>
                        <div className="rounded border p-3">
                          <div className="text-xs text-gray-500">Config. correo</div>
                          <div className="text-lg font-semibold">{stats?.resend_configured ? 'Sí' : 'No'}</div>
                        </div>
                      </div>
                    </div>
                    {emailAttempts.length > 0 && (
                      <div>
                        <div className="text-sm font-medium mb-2">Intentos de Email</div>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Acción</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Origen</TableHead>
                                <TableHead>Cargo ID</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {emailAttempts.map((it, idx) => (
                                <TableRow key={`${it?.charge_id || idx}-${idx}`}>
                                  <TableCell>{it?.action || '-'}</TableCell>
                                  <TableCell>{it?.status || '-'}</TableCell>
                                  <TableCell className="break-all">{it?.to_email || '-'}</TableCell>
                                  <TableCell>{it?.source || '-'}</TableCell>
                                  <TableCell className="font-mono text-xs break-all">{it?.charge_id || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <div className="text-xs text-gray-500 mb-1">Error</div>
                <div className="text-sm break-words">{selected.error_message || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Métricas</div>
                <pre className="text-xs p-2 rounded bg-muted/30 overflow-auto max-h-64">
{JSON.stringify(selected.metrics || {}, null, 2)}
                </pre>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Contexto</div>
                <pre className="text-xs p-2 rounded bg-muted/30 overflow-auto max-h-64">
{JSON.stringify(selected.context || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CronRunLogs;
